<template>
    <cart-template
        :class="{ 'overlay prelative': activeCompare }"
        :key="earlyRenewal"
        :isSales="true"
        :customer="customer"
    >
        <template slot="configuration">
            <p class="cart-config-title mb-0">
                {{ $l10n.__('Abonnement de') }} {{ customer.name }}
            </p>
            <p class="mt-1 mb-6 semibold gray-60 fs-3">
                <span>{{ $l10n.__("Date d'activation de l'abonnement") }}</span>
                {{ periodApplianceStart | dateFormatter('SLASHED_DATE', false) }}
            </p>
            <!-- Legacy modules warning -->
            <card
                background="alert-05"
                borderColor="alert-50"
                :padding="2"
                class="br mb-4 fs-3 legacy-products"
                v-if="legacyProducts.length > 0"
            >
                <div class="pl-2 pr-2">
                    <p class="bold mt-0 mb-1">
                        {{ $l10n.__('Des anciens modules sont actifs sur ce compte :') }}
                    </p>
                    <ul class="mb-0 mt-0 pl-4 module-list">
                        <li v-for="legacyProduct in legacyProducts" :key="legacyProduct.code">
                            {{ legacyProduct.name }}
                        </li>
                    </ul>
                </div>
            </card>
            <!-- Current Subscription details -->
            <subscription-summary context="renew" isSales />

            <el-form
                ref="saleCartForm"
                label-position="top"
                :rules="setRules"
                :model="modelForm"
                class="mt-6"
            >
                <date-config store="renew"></date-config>
                <h1>{{ $l10n.__('Configuration de l’offre de renouvellement') }}</h1>

                <article>
                    <header class="flex-level ai-center mt-6 mb-4">
                        <h2 class="m-0">{{ $l10n.__('Le plan') }}</h2>
                        <a href="https://go.sellsy.com/tarifs/" target="_blank" class="ml-3 link">{{
                            $l10n.__('Comparer les plans')
                        }}</a>
                    </header>
                    <el-form-item>
                        <el-radio-group v-model="licencePlan">
                            <el-radio
                                v-for="plan in plans"
                                :disabled="plan.disabled"
                                :key="plan.code"
                                :label="plan.code"
                            >
                                <tag-label
                                    class="semibold uppercase"
                                    :bgColor="plan.color"
                                    :disabled="plan.disabled"
                                >
                                    <template slot="label">{{ plan.name }}</template>
                                </tag-label>
                            </el-radio>
                        </el-radio-group>
                    </el-form-item>
                </article>

                <span class="separator block my-6"></span>

                <article>
                    <el-form-item prop="products" class="flex-column-content">
                        <header class="flex-row jc-space ai-center mb-5">
                            <h2 class="m-0">{{ $l10n.__('Les licences') }}</h2>
                            <radio-input
                                mode="edit"
                                radio-style="button"
                                :value="recurrency"
                                :choices="recurrencies"
                                @change="updateRecurrency"
                            />
                        </header>
                        <licence-product
                            class="mb-5"
                            v-for="product in availablesProducts"
                            :key="product.code"
                            :value="products[product.id]"
                            :product="product"
                            :plan="plan"
                            userType="sale"
                            :numformat="numformat"
                            :saleLimitation="renewOnOlderPricingVersion"
                            @input="updateProductQuantity"
                        >
                        </licence-product>
                        <template slot="error" slot-scope="{ error }">
                            <div
                                class="absolute wp-10 mt-0 mb-5 alert alert-danger"
                                style="order: -1"
                            >
                                {{ error }}
                            </div>
                        </template>
                    </el-form-item>
                </article>

                <span class="separator block my-6"></span>

                <article>
                    <el-form-item>
                        <header>
                            <h2 class="mb-5">{{ $l10n.__('Modules supplémentaires') }}</h2>
                        </header>
                        <bonus-pack
                            class="mb-5"
                            v-for="product in [...availablesPacks, ...currentGroupedPacks]"
                            :key="product.code"
                            :value="products[product.id]"
                            :product="product"
                            :plan="plan"
                            :numformat="numformat"
                            userType="sale"
                            :sourceLimitation="false"
                            @input="updateProductQuantity"
                            @changePackPlan="switchPackPlan"
                        >
                        </bonus-pack>
                    </el-form-item>
                </article>

                <span class="separator block my-6"></span>

                <staffs class="mb-6" v-if="renewOnOlderPricingVersion" />

                <article>
                    <header>
                        <h2 class="mt-0 mb-5">{{ $l10n.__('Paiement') }}</h2>
                    </header>
                    <el-form-item :label="$t.__('common.labels.payment_method')">
                        <el-radio-group v-model="payment" prop="paymentMethod">
                            <el-radio
                                class="black"
                                v-for="payment in paymentMethods"
                                :key="payment.id"
                                :label="payment.id"
                                :disabled="disableWithdrawal(payment.id)"
                                >{{ payment.label }}</el-radio
                            >
                        </el-radio-group>
                    </el-form-item>
                </article>

                <span class="separator block my-6"></span>

                <!-- Billing Contact -->
                <sam-cart-contact-form
                    :value="contact"
                    @input="updateContactForm"
                    :errors="errors"
                ></sam-cart-contact-form>

                <span class="separator block my-6"></span>

                <!-- Billing Settings -->
                <h2 class="mt-0 mb-5">{{ $l10n.__('Informations de facturation') }}</h2>
                <address-input v-model="addressSettings">
                    <template slot="manualAddress" slot-scope="{ data, updateCountry }">
                        <el-form-item
                            prop="billingSettings.line1"
                            :label="$t.__('common.labels.street_name')"
                        >
                            <el-input v-model="data.line1" autocomplete="off"></el-input>
                        </el-form-item>
                        <el-form-item :label="$t.__('common.labels.more_address_informations')">
                            <el-input v-model="data.line2" autocomplete="off"></el-input>
                        </el-form-item>
                        <div class="flex-row">
                            <el-form-item
                                class="wp-3"
                                prop="billingSettings.postalCode"
                                :label="$t.__('common.labels.postal_code')"
                            >
                                <el-input v-model="data.postalCode" autocomplete="off"></el-input>
                            </el-form-item>
                            <el-form-item
                                class="wp-7 ml-3"
                                prop="billingSettings.city"
                                :label="$t.__('common.labels.city')"
                            >
                                <el-input v-model="data.city" autocomplete="off"></el-input>
                            </el-form-item>
                        </div>
                        <div class="flex-row">
                            <el-form-item class="wp-5" :label="$t.__('common.labels.state')">
                                <el-input
                                    v-model="data.administrative"
                                    autocomplete="off"
                                ></el-input>
                            </el-form-item>
                            <el-form-item
                                class="wp-5 ml-3"
                                prop="billingSettings.country"
                                :label="$t.__('common.labels.country')"
                            >
                                <country-input
                                    v-bind:default-value="{
                                        code: data.countryCode,
                                        name: data.country,
                                    }"
                                    @change="updateCountry"
                                >
                                </country-input>
                            </el-form-item>
                        </div>
                    </template>
                    <template slot="algoliaWrapper" slot-scope="{ methods, props, data }">
                        <el-form-item
                            prop="billingSettings.line1"
                            :label="$t.__('common.labels.street_name')"
                            :class="{ hide: data.currentValue.manualMode }"
                        >
                            <algolia-input
                                :element-name="props.value._id"
                                place-type="address"
                                @bubble-address="methods.captureAddress"
                            ></algolia-input>
                            <CompassLink
                                :label="props.manuallyLabel"
                                type="primary"
                                size="medium"
                                @click="methods.switchMode"
                            />
                        </el-form-item>
                    </template>
                </address-input>
                <div class="flex-row">
                    <el-form-item
                        class="wp-5"
                        prop="billingLocale"
                        :label="$t.__('common.labels.invoicing_language')"
                    >
                        <el-select v-model="contact.billingLocale" class="wp-10">
                            <el-option
                                v-for="locale in billingLocales"
                                :key="locale.code"
                                :label="locale.name"
                                :value="locale.code"
                            >
                            </el-option>
                        </el-select>
                    </el-form-item>
                    <el-form-item
                        class="wp-5 ml-4"
                        :label="$t.__('common.labels.tax_number')"
                        prop="vatIntra"
                    >
                        <el-input v-model="tvaIntra"></el-input>
                    </el-form-item>
                </div>

                <span class="separator block my-6"></span>

                <!-- Custom Message Settings -->
                <h2 class="mt-0 mb-5">{{ $l10n.__('Personnalisation des emails') }}</h2>

                <custom-message v-if="contact.name" context="renew"></custom-message>
            </el-form>
        </template>
        <template slot="resume">
            <section class="pabsolute compare aright" v-if="!renewOnOlderPricingVersion">
                <el-button
                    class="compare-btn warning-05-bkg warning-100 b-0 px-2 h-32"
                    @click="activeCompare = !activeCompare"
                >
                    <span class="flex-row ai-center jc-center">
                        <i
                            class="mr-1"
                            :class="{
                                'icon-chevron-right': activeCompare,
                                'icon-chevron-left': !activeCompare,
                            }"
                        ></i>
                        <span class="semibold">{{ $l10n.__('Comparer') }}</span>
                    </span>
                </el-button>
                <!-- COMPARE CART WITH VARIATION -->
                <transition name="xslide-fade">
                    <compare-upsell-sheet
                        class="compare-sheet aleft"
                        v-if="activeCompare && !renewOnOlderPricingVersion"
                        store="renew"
                    >
                    </compare-upsell-sheet>
                </transition>
            </section>
            <cart-summary context="renew" :userSale="true">
                <template slot="licences">
                    <ul class="ov-auto mb-0 cart-recap-products">
                        <!-- Products Summary -->
                        <li
                            v-for="(product, index) in productsWithLicences"
                            :key="product.code"
                            class="flex-row"
                            :class="{
                                'mb-to-free-licences':
                                    !freeLicencesByProduct(product.id) &&
                                    previousFreeLicencesByProduct(product.id),
                                'mb-2': index + 1 !== productsWithLicences.length,
                            }"
                        >
                            <div>
                                <p class="grey m-0 fs-3">
                                    {{
                                        $l10n.n__(
                                            '%(total)s licence %(productsName)s',
                                            '%(total)s licences %(productsName)s',
                                            products[product.id].quantity,
                                            {
                                                total: products[product.id].quantity,
                                                productsName: product.title,
                                            },
                                        )
                                    }}
                                </p>
                                <span v-if="freeLicencesByProduct(product.id)" class="grey">{{
                                    $l10n.n__(
                                        'Dont %s offerte',
                                        'Dont %s offertes',
                                        freeLicencesByProduct(product.id),
                                        freeLicencesByProduct(product.id),
                                    )
                                }}</span>
                            </div>
                            <template v-if="products[product.id].total">
                                <p
                                    v-if="freeLicencesByProduct(product.id)"
                                    class="flex-item-right my-0"
                                >
                                    <span class="strike grey semibold">{{
                                        products[product.id].total.monthly.total_amount
                                            | floatNumber
                                            | numberformatWithOptions(numformat, true)
                                    }}</span>
                                    <span class="success-50 fs-3 pl-2 semibold"
                                        >{{
                                            products[product.id].total.monthly
                                                .total_amount_discounted
                                                | floatNumber
                                                | numberformatWithOptions(numformat, true)
                                        }}<span class="fs-2">{{ $l10n.__('/mois') }}</span></span
                                    >
                                </p>
                                <p v-else class="flex-item-right my-0 fs-3 semibold grey">
                                    {{
                                        products[product.id].total.monthly.total_amount_discounted
                                            | floatNumber
                                            | numberformatWithOptions(numformat, true)
                                    }}<span class="fs-2">{{ $l10n.__('/mois') }}</span>
                                </p>
                            </template>
                        </li>
                        <!-- Packs Summary -->
                        <li
                            v-for="(product, index) in [
                                ...packsWithLicences,
                                ...groupedPacksWithLicences,
                            ]"
                            :key="product.code"
                            class="flex-row"
                            :class="{
                                'mb-2':
                                    index + 1 !==
                                    [...packsWithLicences, ...groupedPacksWithLicences].length,
                                'mt-2': index === 0,
                            }"
                        >
                            <div>
                                <p class="grey m-0 fs-3">
                                    {{
                                        $l10n.__('Module %(productsName)s', {
                                            productsName: !!product.groups.length
                                                ? product.printableName
                                                : product.title,
                                        })
                                    }}
                                </p>
                            </div>
                            <template v-if="products[product.id].total">
                                <!-- Discount detail -->
                                <p
                                    v-if="
                                        products[product.id].total.monthly.total_amount_discounted <
                                        products[product.id].total.monthly.total_amount
                                    "
                                    class="flex-item-right my-0 semibold"
                                >
                                    <span class="strike grey semibold">{{
                                        products[product.id].total.monthly.total_amount
                                            | floatNumber
                                            | numberformatWithOptions(numformat, true)
                                    }}</span>
                                    <span class="success-50 fs-3 pl-2">
                                        <template
                                            v-if="
                                                products[product.id].total.monthly
                                                    .total_amount_discounted !== 0
                                            "
                                        >
                                            {{
                                                products[product.id].total.monthly
                                                    .total_amount_discounted
                                                    | floatNumber
                                                    | numberformatWithOptions(numformat, true)
                                            }}<span class="fs-2">{{ $l10n.__('/mois') }}</span>
                                        </template>
                                        <template v-else>{{ $l10n.__('Offert') }}</template>
                                    </span>
                                </p>
                                <!-- Base price -->
                                <p v-else class="flex-item-right my-0 fs-3 semibold grey">
                                    {{
                                        products[product.id].total.monthly.total_amount_discounted
                                            | floatNumber
                                            | numberformatWithOptions(numformat, true)
                                    }}<span class="fs-2">{{ $l10n.__('/mois') }}</span>
                                </p>
                            </template>
                        </li>
                    </ul>
                </template>
                <template slot="subtotal">
                    <div class="flex-column fs-3 cart-recap-total-label">
                        <span class="mb-2">{{ $l10n.__('Total HT avant remise') }}</span>
                        <span class="mb-2">{{ $l10n.__('Remise') }}</span>
                        <span class="mb-2">{{ $l10n.__('Total HT') }}</span>
                        <span class="mb-2" v-if="taxRate && taxRate !== 0"
                            >{{ $l10n.__('TVA') }} {{ taxRate }}%</span
                        >
                    </div>
                    <div class="flex-column aright fs-3">
                        <span class="mb-2"
                            >{{
                                totalYearly.total_amount
                                    | floatNumber
                                    | numberformatWithOptions(numformat, true)
                            }}<span class="fs-2">{{ $l10n.__('/an') }}</span></span
                        >
                        <span class="mb-2"
                            >{{
                                totalYearly.total_amount_discount
                                    | floatNumber
                                    | numberformatWithOptions(numformat, true)
                            }}<span class="fs-2">{{ $l10n.__('/an') }}</span></span
                        >
                        <span class="mb-2"
                            >{{
                                totalYearly.total_amount_discounted
                                    | floatNumber
                                    | numberformatWithOptions(numformat, true)
                            }}<span class="fs-2">{{ $l10n.__('/an') }}</span></span
                        >
                        <span class="mb-2" v-if="taxRate && taxRate !== 0">{{
                            totalYearly.total_amount_vat
                                | floatNumber
                                | numberformatWithOptions(numformat, true)
                        }}</span>
                    </div>
                    <div class="flex-column aright fs-3">
                        <span class="mb-2"
                            >{{
                                totalMonthly.total_amount
                                    | floatNumber
                                    | numberformatWithOptions(numformat, true)
                            }}<span class="fs-2">{{ $l10n.__('/mois') }}</span></span
                        >
                        <span class="mb-2"
                            >{{
                                totalMonthly.total_amount_discount
                                    | floatNumber
                                    | numberformatWithOptions(numformat, true)
                            }}<span class="fs-2">{{ $l10n.__('/mois') }}</span></span
                        >
                        <span class="mb-2"
                            >{{
                                totalMonthly.total_amount_discounted
                                    | floatNumber
                                    | numberformatWithOptions(numformat, true)
                            }}<span class="fs-2">{{ $l10n.__('/mois') }}</span></span
                        >
                        <span class="mb-2" v-if="taxRate && taxRate !== 0">{{
                            totalMonthly.total_amount_vat
                                | floatNumber
                                | numberformatWithOptions(numformat, true)
                        }}</span>
                    </div>
                </template>
                <template slot="total">
                    <div class="flex-row jc-space">
                        <p class="fs-4 my-0 bold cart-recap-total-label">
                            {{ $l10n.__('Total TTC') }}
                        </p>
                        <p class="fs-4 my-0 aright bold">
                            {{
                                totalYearly.total_amount_tax_included
                                    | floatNumber
                                    | numberformatWithOptions(numformat, true)
                            }}<span class="fs-2">{{ $l10n.__('/an') }}</span>
                        </p>
                        <p class="fs-4 my-0 aright bold">
                            {{
                                totalMonthly.total_amount_tax_included
                                    | floatNumber
                                    | numberformatWithOptions(numformat, true)
                            }}<span class="fs-2">{{ $l10n.__('/mois') }}</span>
                        </p>
                    </div>
                    <div v-if="earlyRenewal" class="earlyRenewCedit mt-3">
                        <span class="separator block mb-3"></span>
                        <div class="avoir flex-row jc-space">
                            <p class="semibold fs-3 my-0 lh-20">{{ $l10n.__('Avoir estimé') }}</p>
                            <p class="semibold fs-3 my-0 lh-20">
                                -
                                {{
                                    earlyRenewCredit
                                        | floatNumber
                                        | numberformatWithOptions(numformat, true)
                                }}
                            </p>
                        </div>
                        <p class="fs-12 gray-60 mb-0 mt-1 semibold lh-s" v-if="isYearly">
                            {{
                                $l10n.__(
                                    'La prochaine échéance sera le %(nextPaymentDate)s à %(nextPaymentAmount)s',
                                    {
                                        nextPaymentDate: nextYearlyPaymentDate,
                                        nextPaymentAmount: nextYearlyPaymentAmount,
                                    },
                                )
                            }}
                        </p>
                        <p class="fs-12 gray-60 mb-0 mt-1 semibold lh-s" v-if="isMonthly">
                            {{
                                $l10n.__(
                                    'La prochaine échéance sera le %(nextPaymentDate)s à %(nextPaymentAmount)s, puis les %(dueDates)s échéances suivantes à %(dueDateAmount)s',
                                    {
                                        nextPaymentDate: nextMonthlyPaymentDate,
                                        nextPaymentAmount: nextMonthlyPaymentAmount,
                                        dueDates: numberOfDueDates,
                                        dueDateAmount: dueDatesAmount,
                                    },
                                )
                            }}
                        </p>
                    </div>
                </template>
                <template slot="actions">
                    <CompassButton
                        :label="$t.__('common.actions.send_to_client')"
                        type="primary"
                        :loading="computeLoading"
                        @click="submitForm('saleCartForm', 'action')"
                        class="wp-10 mt-4"
                    />
                </template>
            </cart-summary>
        </template>
    </cart-template>
</template>

<script>
import Address from 'vcomponents/inputs/custom/Address.vue';
import PricingV4Module from 'stores/sellsyaccountmanager/v4/renew';
import AddressRules from 'vcomponents/forms/rules/AddressRules.js';
import CountryInput from 'vcomponents/inputs/CountryInput.vue';
import AlgoliaInput from 'vcomponents/inputs/AlgoliaInput.vue';
import { createNamespacedHelpers } from 'vuex';
import VatIdentifierValidator from 'Services/VatIdentifierValidator';
import TagLabel from 'vcomponents/utilities/tags/TagLabel.vue';
import LicenceProduct from 'vcomponents/pages/account/LicenceProduct.vue';
import BonusPack from 'vcomponents/pages/account/BonusPack.vue';
import IconSvg from 'vcomponents/icons/IconSvg.vue';
import IconSellsyCloud from 'vcomponents/icons/IconSvgLogoSellsyCloud.vue';
import IconSellsyText from 'vcomponents/icons/IconSvgLogoSellsyText.vue';
import FormMixin from 'mixins/FormMixins';
import { fetchMetadata } from 'api/sellsyaccountmanager';
import CustomMessage from 'vcomponents/pages/account/sale/CustomMessage.vue';
import SubscriptionSummary from './SubscriptionSummary.vue';
import Staffs from './Staffs.vue';

import RadioInput from 'vcomponents/inputs/RadioInput.vue';
import SamCartContactForm from 'vcomponents/pages/account/SamCartContactForm.vue';
import CartTemplate from 'vcomponents/pages/account/CartTemplate.vue';
import CartSummary from 'vcomponents/pages/account/CartSummary.vue';
import DateConfig from 'vcomponents/pages/account/sale/DateConfig.vue';
import Card from 'vcomponents/Card.vue';
import CompareUpsellSheet from 'vcomponents/pages/account/upsell/CompareUpsellSheet.vue';
import { goToUrl } from 'js/vue/helpers';

const renewCart = createNamespacedHelpers('renewCart');
const corp = createNamespacedHelpers('corp');
export default {
    components: {
        SamCartContactForm,
        'address-input': Address,
        AlgoliaInput,
        CountryInput,
        RadioInput,
        TagLabel,
        LicenceProduct,
        IconSvg,
        IconSellsyCloud,
        IconSellsyText,
        CartTemplate,
        CartSummary,
        Card,
        CompareUpsellSheet,
        BonusPack,
        CustomMessage,
        SubscriptionSummary,
        Staffs,
        DateConfig,
    },
    mixins: [FormMixin],
    async beforeRouteEnter(to, from, next) {
        await fetchMetadata(
            to.params.customerId,
            to.query.isLegacy ? 'legacy_renew' : 'renew',
            'saas',
        )
            .then((res) => {
                next((vm) => {
                    vm.setMetadata(res);
                    vm.setSubscriptionUses(res.cart);
                    vm.fetchCustomerData(to.params.corpId);
                    if (to.params.id !== 'new') {
                        vm.fetchCart(to.params.id);
                    }
                });
            })
            .catch((err) => {
                next(false);
            });
    },
    data() {
        return {
            rules: {
                name: [{ required: true, message: this.$l10n.__('Veuillez renseigner un nom') }],
                forename: [
                    { required: true, message: this.$l10n.__('Veuillez renseigner un prénom') },
                ],
                phone: [
                    {
                        required: true,
                        message: this.$l10n.__('Veuillez renseigner un numéro de téléphone'),
                    },
                    {
                        min: 4,
                        message: this.$l10n.__('Veuillez renseigner un numéro de téléphone valide'),
                    },
                ],
                billingLocale: [
                    {
                        required: true,
                        message: this.$l10n.__('Veuillez renseigner une langue de facturation'),
                    },
                ],
                email: [
                    {
                        required: true,
                        type: 'email',
                        message: this.$l10n.__('Veuillez renseigner une adresse email'),
                    },
                ],
                billingSettings: {
                    ...AddressRules,
                },
                vatIntra: [
                    {
                        validator: (rule, value, callback) => {
                            /**
                             * Check TVA Intra format with associated country, but no require.
                             */
                            if (
                                value === '' ||
                                _.isNil(value) ||
                                _.isUndefined(this.billingSettings.countryCode)
                            )
                                return callback();

                            if (_.isNull(this.billingSettings.countryCode))
                                return callback(new Error(this.$l10n.__('Aucun pays sélectionné')));

                            let validator = new VatIdentifierValidator(
                                this.billingSettings.countryCode,
                            );
                            if (!validator.isValid(value)) {
                                return callback(
                                    new Error(
                                        this.$l10n.__(
                                            'Le format du n° de tva ne correspond pas à celui du pays sélectionné',
                                        ),
                                    ),
                                );
                            }
                            callback();
                        },
                        trigger: 'change',
                    },
                ],
                products: [
                    {
                        validator: (rule, value, callback) => {
                            /**
                             * Cart must get 2 paid licences or 1 if allowed by permission
                             */
                            if (this.customer.allowsOneLicense && this.quantityPaidLicences < 1) {
                                return callback(
                                    new Error(
                                        this.$l10n.__(
                                            'Veuillez sélectionner au minimum 1 licence payante',
                                        ),
                                    ),
                                );
                            } else if (
                                !this.customer.allowsOneLicense &&
                                this.quantityPaidLicences < 2
                            ) {
                                return callback(
                                    new Error(
                                        this.$l10n.__(
                                            'Veuillez sélectionner au minimum 2 licences payantes',
                                        ),
                                    ),
                                );
                            }

                            callback();
                        },
                        trigger: 'change',
                    },
                ],
                paymentMethod: [
                    {
                        required: true,
                        message: this.$l10n.__('Veuillez renseigner un moyen de paiement'),
                    },
                ],
            },
            customMessageValidator: {
                customMessage: [
                    {
                        validator: (rule, value, callback) => {
                            let customMsg = this.customMessage.match(/<p>(.*?)<\/p>/g);
                            if (!customMsg || customMsg[0].trim() === '<p></p>') {
                                return callback(
                                    new Error(
                                        this.$l10n.__(
                                            'Ce champ ne peut pas être vide lorsque vous indiquez vouloir joindre un message personnalisé',
                                        ),
                                    ),
                                );
                            }
                            callback();
                        },
                    },
                ],
            },
            activeCompare: false,
        };
    },
    computed: {
        ...corp.mapGetters(['corpName']),
        ...renewCart.mapGetters([
            'modelForm',
            'contact',
            'billingLocales',
            'periodApplianceStart',
            'billingSettings',
            'recurrencies',
            'recurrency',
            'vatIntra',
            'customer',
            'plans',
            'currentPlan',
            'availablesProducts',
            'availablesPacks',
            'products',
            'productsWithLicences',
            'currentGroupedPacks',
            'groupedPacksWithLicences',
            'packsWithLicences',
            'previousCart',
            'previousCurrentPlanLabel',
            'plan',
            'isYearly',
            'isMonthly',
            'freeLicencesByProduct',
            'previousFreeLicencesByProduct',
            'totalYearly',
            'totalMonthly',
            'computeLoading',
            'taxRate',
            'paymentMethod',
            'paymentMethods',
            'labelPlan',
            'quantityPaidLicences',
            'cartId',
            'numformat',
            'renewOnOlderPricingVersion',
            'earlyRenewal',
            'earlyRenewDate',
            'subscriptionUses',
            'subscriptionUseLabel',
            'previousSubcribedProducts',
            'isCustomMessageUsed',
            'customMessage',
            'legacyProducts',
            'earlyRenewCredit',
        ]),
        addressSettings: {
            get() {
                return this.billingSettings;
            },
            set(value) {
                this.$store.commit('renewCart/setBillingSettings', value);
                this.$store.dispatch('renewCart/computeCart');
            },
        },
        tvaIntra: {
            get() {
                return this.vatIntra;
            },
            set(value) {
                this.$store.commit('renewCart/setVatIntra', value);
                this.$store.dispatch('renewCart/computeCart');
            },
        },
        licencePlan: {
            get() {
                return this.currentPlan;
            },
            set(value) {
                this.$store.dispatch('renewCart/updatePlan', value);
            },
        },
        labelPlan() {
            return this.isYearly
                ? this.$l10n.__('Abonnement annuel')
                : this.$l10n.__('Abonnement mensuel');
        },
        payment: {
            get() {
                return this.paymentMethod;
            },
            set(value) {
                this.$store.commit('renewCart/setPaymentMethod', value);
            },
        },
        leftDurationSubscription() {
            const serviceEnd = moment(this.previousCart.serviceEndDate);
            const now = moment();
            const months = serviceEnd.diff(now, 'months');
            now.add(months, 'months');
            const days = serviceEnd.diff(now, 'days');

            return this.$l10n.__(`dans ${months} mois et ${days} jours`);
        },
        /**
         * Add custom message if custom Message is enable
         */
        setRules() {
            if (this.isCustomMessageUsed) {
                const newRules = Object.assign({ ...this.rules }, this.customMessageValidator);
                return newRules;
            } else {
                return this.rules;
            }
        },
        nextYearlyPaymentDate() {
            return this.totalYearly.upcoming_deadline
                ? moment(this.totalYearly.upcoming_deadline.date).format('ll')
                : '-';
        },
        nextYearlyPaymentAmount() {
            const amount = this.totalYearly.upcoming_deadline?.amount ?? 0;
            return this.$options.filters.numberformatWithOptions(
                this.$options.filters.floatNumber(amount),
                {},
                true,
            );
        },
        firstMonthlyPayment() {
            return this.totalMonthly.upcoming_deadlines.find((month) => month.amount > 0);
        },
        nextMonthlyPaymentDate() {
            if (!this.totalMonthly.upcoming_deadlines) {
                return '-';
            }
            return moment(this.firstMonthlyPayment.date).format('ll');
        },
        nextMonthlyPaymentAmount() {
            if (!this.totalMonthly.upcoming_deadlines) {
                return '-';
            }
            return this.$options.filters.numberformatWithOptions(
                this.$options.filters.floatNumber(this.firstMonthlyPayment.amount),
                {},
                true,
            );
        },
        numberOfDueDates() {
            if (!this.totalMonthly.upcoming_deadlines) {
                return '-';
            }
            const months = this.totalMonthly.upcoming_deadlines.filter(
                (month) => month.amount > 0 && month.date !== this.firstMonthlyPayment.date,
            );
            return months.length;
        },
        dueDatesAmount() {
            if (!this.totalMonthly.upcoming_deadlines) {
                return '-';
            }
            const months = this.totalMonthly.upcoming_deadlines.filter(
                (month) => month.amount > 0 && month.date !== this.firstMonthlyPayment.date,
            );
            return this.$options.filters.numberformatWithOptions(
                this.$options.filters.floatNumber(months[0].amount),
                {},
                true,
            );
        },
    },
    watch: {
        '$route.query.early'(value) {
            this.setEarlyRenewal(value === 'true');
        },
    },
    created() {
        this.$store.registerModule('renewCart', PricingV4Module);
        this.setCustomerId(this.$route.params.customerId);
        this.setCorporationId(this.$route.params.corpId);
        this.setSource('sales');
        this.setEarlyRenewal(this.$route.query.early === 'true');
    },
    beforeDestroy() {
        this.$store.unregisterModule('renewCart');
    },
    methods: {
        ...renewCart.mapMutations([
            'setCustomerId',
            'setCorporationId',
            'setSource',
            'setSubscriptionUses',
            'setEarlyRenewal',
        ]),
        ...renewCart.mapActions([
            'fetchMetadata',
            'fetchCustomerData',
            'updateContact',
            'updateRecurrency',
            'updatePlan',
            'updateProductQuantity',
            'computeCart',
            'createCart',
            'fetchCart',
            'updateCart',
            'setMetadata',
            'switchPackPlan',
        ]),
        updateContactForm(e) {
            this.updateContact({
                ...this.contact,
                ...e,
            });
        },
        back() {
            history.back();
        },
        action() {
            if (this.$route.params.id !== 'new' || !_.isNil(this.cartId)) {
                // Update
                this.update();
            } else {
                this.create();
            }
        },
        create() {
            this.createCart()
                .then((res) => {
                    Push.success(this.$l10n.__('Panier envoyé avec succès'));
                    goToUrl(
                        this.$router.resolve({
                            name: 'third.overview.samCarts',
                            params: { type: 'client', id: this.$route.params.thirdId },
                        }).href,
                    );
                })
                .catch((errors) => {
                    this.errors.setErrors(errors);
                });
        },
        update() {
            this.updateCart(this.cartId)
                .then(() => {
                    Push.success(this.$l10n.__('Panier modifié avec succès'));
                    goToUrl(
                        this.$router.resolve({
                            name: 'third.overview.samCarts',
                            params: { type: 'client', id: this.$route.params.thirdId },
                        }).href,
                    );
                })
                .catch((errors) => {
                    this.errors.setErrors(errors);
                });
        },
        disableWithdrawal(id) {
            return id === 'manual' && this.isMonthly;
        },
    },
};
</script>

<style lang="scss">
@import '~@sellsy/styleguide/src/_variables';
.page-sam-v4-sale-upsell-cart-form {
    .cart-recap-products {
        min-height: 180px;
    }

    .mb-to-free-licences {
        margin-bottom: 22px !important;
    }
}

.cart-config-context {
    width: 180px;
}

.el-form-item.flex-column-content {
    .el-form-item__content {
        display: flex;
        flex-direction: column;
    }
}

.cart.overlay .cart-config {
    &:before {
        content: '';
        display: block;
        position: absolute;
        top: 0;
        bottom: 0;
        right: 0;
        left: 0;
        background: $white;
        opacity: 0.9;
        z-index: 2;
    }
}

.cart-recap {
    .compare {
        right: 100%;
        top: 28px;

        &-btn,
        &-sheet {
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
        }
    }
    .xslide-fade-enter,
    .xslide-fade-leave-to {
        transform: translateX(100%);
        flex: 0;
        opacity: 0;
    }
}

.legacy-products {
    line-height: 20px;
    max-width: 620px;
}

.module-list {
    list-style-type: disc;
}
</style>
